import React, { useState, useEffect, useReducer, Suspense } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth } from '../App';
import DashboardNavDropdown from './DashboardNavDropdown';

// Lazy load components for better performance
const NotificationCenter = React.lazy(() => import('./NotificationCenter'));
const EnhancedSearchBar = React.lazy(() => import('./EnhancedSearchBar'));
const AnalyticsDashboard = React.lazy(() => import('./AnalyticsDashboard'));
const RealTimeNotifications = React.lazy(() => import('./RealTimeNotifications'));
const ChatSystem = React.lazy(() => import('./ChatSystem'));
const EnhancedReviewSystem = React.lazy(() => import('./EnhancedReviewSystem'));
const VideoCallButton = React.lazy(() => import('./VideoCallButton'));
const AdvancedBookingSystem = React.lazy(() => import('./AdvancedBookingSystem'));
const LanguageSwitcher = React.lazy(() => import('./LanguageSwitcher'));
const AutomatedInvoicingSystem = React.lazy(() => import('./AutomatedInvoicing'));
const SmartMatchingSystem = React.lazy(() => import('./SmartMatchingSystem'));
const AiChatbotSupport = React.lazy(() => import('./AiChatbotSupport'));
const AiContentGeneration = React.lazy(() => import('./AiContentGeneration'));

// Dashboard state reducer for better state management
const dashboardReducer = (state, action) => {
  switch (action.type) {
    case 'SET_ACTIVE_TAB':
      return { ...state, activeTab: action.payload };
    case 'SET_SERVICES':
      return { ...state, services: action.payload };
    case 'SET_PRODUCTS':
      return { ...state, products: action.payload };
    case 'SET_LIVE_BIDS':
      return { ...state, liveBids: action.payload };
    case 'SET_PROVIDERS':
      return { ...state, providers: action.payload };
    case 'TOGGLE_MODAL':
      return { ...state, modals: { ...state.modals, [action.payload]: !state.modals[action.payload] } };
    case 'SET_LOADING':
      return { ...state, loading: { ...state.loading, [action.payload.key]: action.payload.value } };
    case 'SET_SEARCH_FILTERS':
      return { ...state, searchFilters: { ...state.searchFilters, ...action.payload } };
    default:
      return state;
  }
};

const initialState = {
  activeTab: 'services',
  services: [],
  products: [],
  liveBids: [],
  providers: [],
  modals: {
    analytics: false,
    chat: false,
    reviews: false,
    videoCall: false,
    booking: false,
    invoicing: false,
    matching: false,
    chatbot: false,
    contentGeneration: false
  },
  loading: {
    services: false,
    products: false,
    liveBids: false,
    providers: false
  },
  searchFilters: {
    category: 'all',
    location: '',
    priceRange: 'all'
  }
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(dashboardReducer, initialState);
  const [featuresTab, setFeaturesTab] = useState('core');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (user) {
      fetchServices();
      fetchProducts();
      fetchLiveBids();
      fetchProviders();
    }
  }, [user]);

  // Re-fetch services when search filters change
  useEffect(() => {
    if (user) {
      fetchServices();
    }
  }, [state.searchFilters, searchTerm]);

  const fetchServices = async () => {
    dispatch({ type: 'SET_LOADING', payload: { key: 'services', value: true } });
    try {
      const token = localStorage.getItem('token');
      
      // Build query parameters for filtering
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (state.searchFilters.category && state.searchFilters.category !== 'all') {
        params.append('category', state.searchFilters.category);
      }
      if (state.searchFilters.location && state.searchFilters.location.trim() !== '') {
        params.append('location', state.searchFilters.location);
      }
      
      const queryString = params.toString();
      const url = queryString ? `${backendUrl}/api/services?${queryString}` : `${backendUrl}/api/services`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      dispatch({ type: 'SET_SERVICES', payload: data || [] });
    } catch (error) {
      console.error('Error fetching services:', error);
      dispatch({ type: 'SET_SERVICES', payload: [] });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'services', value: false } });
    }
  };

  const fetchProducts = async () => {
    dispatch({ type: 'SET_LOADING', payload: { key: 'products', value: true } });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      dispatch({ type: 'SET_PRODUCTS', payload: data || [] });
    } catch (error) {
      console.error('Error fetching products:', error);
      dispatch({ type: 'SET_PRODUCTS', payload: [] });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'products', value: false } });
    }
  };

  const fetchLiveBids = async () => {
    dispatch({ type: 'SET_LOADING', payload: { key: 'liveBids', value: true } });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/bids/live`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      dispatch({ type: 'SET_LIVE_BIDS', payload: data || [] });
    } catch (error) {
      console.error('Error fetching live bids:', error);
      dispatch({ type: 'SET_LIVE_BIDS', payload: [] });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'liveBids', value: false } });
    }
  };

  const fetchProviders = async () => {
    dispatch({ type: 'SET_LOADING', payload: { key: 'providers', value: true } });
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/providers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      dispatch({ type: 'SET_PROVIDERS', payload: data || [] });
    } catch (error) {
      console.error('Error fetching providers:', error);
      dispatch({ type: 'SET_PROVIDERS', payload: [] });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { key: 'providers', value: false } });
    }
  };

  const handleTabChange = (tab) => {
    dispatch({ type: 'SET_ACTIVE_TAB', payload: tab });
  };

  const handleSearchFilters = (filters) => {
    dispatch({ type: 'SET_SEARCH_FILTERS', payload: filters });
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (newFilters) => {
    dispatch({ type: 'SET_SEARCH_FILTERS', payload: newFilters });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-teal-900 flex items-center justify-center">
        <Card className="bg-white/10 backdrop-blur-lg border-white/20">
          <CardContent className="p-6 text-center">
            <p className="text-white mb-4">Please log in to access the dashboard</p>
            <Link to="/auth">
              <Button className="bg-gradient-to-r from-blue-500 to-purple-600 text-white">
                Go to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-teal-900">
      {/* Header */}
      <div className="bg-black/20 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <div className="text-2xl">⚡</div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-white">D.C. Technology Corp.</span>
                <span className="text-[10px] text-white/70">ECRA/ESA #7018534</span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              {user.userType === 'customer' && (
                <Link to="/post-job">
                  <Button className="bg-green-600 text-white text-xs xl:text-sm px-3">
                    Post Job
                  </Button>
                </Link>
              )}
              
              <Link to="/payment-requests">
                <Button variant="outline" className="border-white/50 text-white bg-white/10 text-xs xl:text-sm px-3">
                  Payment Requests
                </Button>
              </Link>

              <Link to="/cart">
                <Button className="bg-orange-600 text-white text-xs xl:text-sm px-3">
                  🛒 Cart
                </Button>
              </Link>

              <Suspense fallback={<div className="w-8 h-8 bg-white/10 rounded animate-pulse"></div>}>
                <NotificationCenter />
              </Suspense>

              <DashboardNavDropdown 
                user={user}
                onLogout={logout}
              />
              
              <Button 
                onClick={logout}
                className="text-white bg-red-600 text-xs xl:text-sm px-3"
              >
                Logout
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <div className="relative">
                <details className="group">
                  <summary className="list-none cursor-pointer">
                    <Button className="p-2 text-white bg-white/10">
                      ☰
                    </Button>
                  </summary>
                  <div className="absolute right-0 mt-2 w-48 bg-slate-800/95 backdrop-blur-lg border border-white/20 rounded-lg shadow-lg z-50">
                    <div className="py-2">
                      {user.userType === 'customer' && (
                        <Link to="/post-job" className="block px-4 py-2 text-white hover:bg-white/10 transition-colors">
                          📝 Post Job
                        </Link>
                      )}
                      <Link to="/payment-requests" className="block px-4 py-2 text-white hover:bg-white/10 transition-colors">
                        💳 Payment Requests
                      </Link>
                      <Link to="/cart" className="block px-4 py-2 text-white hover:bg-white/10 transition-colors">
                        🛒 Cart
                      </Link>
                      
                      <div className="border-t border-white/20 my-2"></div>
                      <div className="px-2 py-1">
                        <DashboardNavDropdown 
                          user={user}
                          onLogout={logout}
                          isMobile={true}
                        />
                      </div>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Welcome */}
      <div className="bg-black/10 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <div>
              <h1 className="text-2xl font-bold text-white mb-1">
                Welcome back, {user.name}!
              </h1>
              <p className="text-white/70">
                {user.userType === 'customer' ? 'Find Services or Post a Job' : 
                 user.userType === 'provider' ? 'Manage Your Services' :
                 user.userType === 'admin' ? 'System Administration' :
                 user.userType === 'supervisor' ? 'Platform Oversight' : 'Dashboard'}
              </p>
            </div>
            <Badge className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-3 py-1">
              {user.userType.charAt(0).toUpperCase() + user.userType.slice(1)}
            </Badge>
          </div>
        </div>
      </div>

      {/* Enhanced Search Bar */}
      <div className="bg-black/5 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Suspense fallback={<div className="h-12 bg-white/10 rounded animate-pulse"></div>}>
            <EnhancedSearchBar 
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              onFilterChange={handleFilterChange}
              filters={{
                selectedCategory: state.searchFilters.category || 'all',
                selectedLocation: state.searchFilters.location || '',
                priceRange: { min: '', max: '' },
                minRating: '',
                sortBy: 'newest',
                inStockOnly: false
              }}
              categories={{ 
                services: ['Electrical', 'Plumbing', 'HVAC', 'Roofing', 'Landscaping'],
                products: ['Tools', 'Materials', 'Equipment', 'Safety']
              }}
              showAdvancedFilters={showAdvancedFilters}
              setShowAdvancedFilters={setShowAdvancedFilters}
              onSearch={handleSearchFilters}
            />
          </Suspense>
        </div>
      </div>

      {/* Customer-specific messaging for job posting */}
      {user.userType === 'customer' && (
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Card className="bg-gradient-to-r from-green-500/20 to-emerald-600/20 border-green-300/30">
            <CardContent className="p-6 text-center">
              <h3 className="text-xl font-bold text-white mb-2">Need Custom Work Done?</h3>
              <p className="text-white/80 mb-4">Post a job and get competitive bids from qualified professionals in your area</p>
              <Link to="/post-job">
                <Button className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-2 text-lg">
                  Post a Job Now
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Main Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['services', 'products', 'liveBids', 'providers'].map((tab) => (
            <Button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`px-4 py-2 rounded-lg  ${
                state.activeTab === tab
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                  : 'bg-white/10 text-white'
              }`}
            >
              {tab === 'services' ? '🔧 Services' :
               tab === 'products' ? '🛒 Products' :
               tab === 'liveBids' ? '💼 Live Bids' :
               tab === 'providers' ? '👥 Providers' : tab}
            </Button>
          ))}
        </div>

        {/* Feature Access Tabs */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 mb-4">
            {['core', 'analytics', 'communications', 'business', 'ai'].map((tab) => (
              <Button
                key={tab}
                onClick={() => setFeaturesTab(tab)}
                className={`px-4 py-2 rounded-lg  ${
                  featuresTab === tab
                    ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white'
                    : 'bg-white/10 text-white'
                }`}
              >
                {tab === 'core' ? '🏠 Core' :
                 tab === 'analytics' ? '📊 Analytics' :
                 tab === 'communications' ? '💬 Communications' :
                 tab === 'business' ? '💼 Advanced Business Operations' :
                 tab === 'ai' ? '🤖 AI Features' : tab}
              </Button>
            ))}
          </div>
        </div>

        {/* Feature Content Based on Selected Tab */}
        {featuresTab === 'analytics' && (
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">📈 Analytics Dashboard</h3>
                  <p className="text-white/80 mb-4">View comprehensive analytics and performance metrics</p>
                  <Button 
                    onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'analytics' })}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 text-white"
                  >
                    Open Analytics
                  </Button>
                </CardContent>
              </Card>
            </div>
            
            {/* Analytics Modal */}
            {state.modals.analytics && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-slate-800 rounded-lg border border-white/20 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold text-white">Analytics Dashboard</h2>
                      <Button 
                        onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'analytics' })}
                        className="text-white bg-red-600"
                      >
                        ✕
                      </Button>
                    </div>
                    <Suspense fallback={<div className="h-64 bg-white/10 rounded animate-pulse"></div>}>
                      <AnalyticsDashboard userType={user.userType} />
                    </Suspense>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {featuresTab === 'communications' && (
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {/* Real-Time Notifications */}
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">🔔 Real-Time Notifications</h3>
                  <p className="text-white/80 mb-4">Manage your notification preferences and view live updates</p>
                  <Suspense fallback={<div className="h-32 bg-white/10 rounded animate-pulse"></div>}>
                    <RealTimeNotifications />
                  </Suspense>
                </CardContent>
              </Card>

              {/* Chat System */}
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">💬 Chat System</h3>
                  <p className="text-white/80 mb-4">Communicate with providers and customers in real-time</p>
                  <Button 
                    onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'chat' })}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 text-white"
                  >
                    Open Chat
                  </Button>
                </CardContent>
              </Card>

              {/* Enhanced Review System */}
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">⭐ Enhanced Reviews</h3>
                  <p className="text-white/80 mb-4">Leave detailed reviews with photo support</p>
                  <Button 
                    onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'reviews' })}
                    className="bg-gradient-to-r from-yellow-500 to-orange-600 text-white"
                  >
                    Write Review
                  </Button>
                </CardContent>
              </Card>

              {/* Video Call Button */}
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">📹 Video Calls</h3>
                  <p className="text-white/80 mb-4">Start video calls with providers after bid acceptance</p>
                  <Suspense fallback={<div className="h-10 bg-white/10 rounded animate-pulse"></div>}>
                    <VideoCallButton recipientId="example-provider-id" />
                  </Suspense>
                </CardContent>
              </Card>
            </div>

            {/* Chat Modal */}
            {state.modals.chat && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-slate-800 rounded-lg border border-white/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold text-white">Chat System</h2>
                      <Button 
                        onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'chat' })}
                        className="text-white bg-red-600"
                      >
                        ✕
                      </Button>
                    </div>
                    <Suspense fallback={<div className="h-64 bg-white/10 rounded animate-pulse"></div>}>
                      <ChatSystem currentUser={user} />
                    </Suspense>
                  </div>
                </div>
              </div>
            )}

            {/* Reviews Modal */}
            {state.modals.reviews && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-slate-800 rounded-lg border border-white/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold text-white">Write Review</h2>
                      <Button 
                        onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'reviews' })}
                        className="text-white bg-red-600"
                      >
                        ✕
                      </Button>
                    </div>
                    <Suspense fallback={<div className="h-64 bg-white/10 rounded animate-pulse"></div>}>
                      <EnhancedReviewSystem 
                        targetType="service" 
                        targetId="example-service-id"
                        onReviewSubmitted={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'reviews' })}
                      />
                    </Suspense>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {featuresTab === 'business' && (
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {/* Advanced Booking System */}
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">📅 Advanced Booking</h3>
                  <p className="text-white/80 mb-4">Professional appointment scheduling with calendar integration</p>
                  <Button 
                    onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'booking' })}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 text-white"
                  >
                    Open Booking
                  </Button>
                </CardContent>
              </Card>

              {/* Language Switcher */}
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">🌍 Language Settings</h3>
                  <p className="text-white/80 mb-4">Switch between English, Spanish, and French</p>
                  <Suspense fallback={<div className="h-10 bg-white/10 rounded animate-pulse"></div>}>
                    <LanguageSwitcher />
                  </Suspense>
                </CardContent>
              </Card>

              {/* Automated Invoicing */}
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">📄 Automated Invoicing</h3>
                  <p className="text-white/80 mb-4">Generate professional invoices with PDF export</p>
                  <Button 
                    onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'invoicing' })}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 text-white"
                  >
                    Manage Invoices
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Booking Modal */}
            {state.modals.booking && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-slate-800 rounded-lg border border-white/20 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold text-white">Advanced Booking System</h2>
                      <Button 
                        onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'booking' })}
                        className="text-white bg-red-600"
                      >
                        ✕
                      </Button>
                    </div>
                    <Suspense fallback={<div className="h-64 bg-white/10 rounded animate-pulse"></div>}>
                      <AdvancedBookingSystem 
                        currentUser={user}
                        onBookingComplete={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'booking' })}
                      />
                    </Suspense>
                  </div>
                </div>
              </div>
            )}

            {/* Invoicing Modal */}
            {state.modals.invoicing && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-slate-800 rounded-lg border border-white/20 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold text-white">Automated Invoicing System</h2>
                      <Button 
                        onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'invoicing' })}
                        className="text-white bg-red-600"
                      >
                        ✕
                      </Button>
                    </div>
                    <Suspense fallback={<div className="h-64 bg-white/10 rounded animate-pulse"></div>}>
                      <AutomatedInvoicingSystem 
                        currentUser={user}
                        onInvoiceAction={() => console.log('Invoice action completed')}
                      />
                    </Suspense>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {featuresTab === 'ai' && (
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {/* Smart Matching System */}
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">🎯 Smart Matching</h3>
                  <p className="text-white/80 mb-4">AI-powered provider and service matching</p>
                  <Button 
                    onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'matching' })}
                    className="bg-gradient-to-r from-purple-500 to-pink-600 text-white"
                  >
                    Find Matches
                  </Button>
                </CardContent>
              </Card>

              {/* AI Chatbot Support */}
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">🤖 AI Chatbot</h3>
                  <p className="text-white/80 mb-4">Get instant AI-powered support and assistance</p>
                  <Button 
                    onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'chatbot' })}
                    className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white"
                  >
                    Chat with AI
                  </Button>
                </CardContent>
              </Card>

              {/* AI Content Generation */}
              <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-white mb-4">✨ AI Content</h3>
                  <p className="text-white/80 mb-4">Generate professional content with AI assistance</p>
                  <Button 
                    onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'contentGeneration' })}
                    className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                  >
                    Generate Content
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Smart Matching Modal */}
            {state.modals.matching && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-slate-800 rounded-lg border border-white/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold text-white">Smart Matching System</h2>
                      <Button 
                        onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'matching' })}
                        className="text-white bg-red-600"
                      >
                        ✕
                      </Button>
                    </div>
                    <Suspense fallback={<div className="h-64 bg-white/10 rounded animate-pulse"></div>}>
                      <SmartMatchingSystem 
                        currentUser={user}
                        onMatchFound={() => console.log('Match found')}
                      />
                    </Suspense>
                  </div>
                </div>
              </div>
            )}

            {/* Chatbot Modal */}
            {state.modals.chatbot && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-slate-800 rounded-lg border border-white/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold text-white">AI Chatbot Support</h2>
                      <Button 
                        onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'chatbot' })}
                        className="text-white bg-red-600"
                      >
                        ✕
                      </Button>
                    </div>
                    <Suspense fallback={<div className="h-64 bg-white/10 rounded animate-pulse"></div>}>
                      <AiChatbotSupport currentUser={user} />
                    </Suspense>
                  </div>
                </div>
              </div>
            )}

            {/* Content Generation Modal */}
            {state.modals.contentGeneration && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-slate-800 rounded-lg border border-white/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold text-white">AI Content Generation</h2>
                      <Button 
                        onClick={() => dispatch({ type: 'TOGGLE_MODAL', payload: 'contentGeneration' })}
                        className="text-white bg-red-600"
                      >
                        ✕
                      </Button>
                    </div>
                    <Suspense fallback={<div className="h-64 bg-white/10 rounded animate-pulse"></div>}>
                      <AiContentGeneration 
                        currentUser={user}
                        onContentGenerated={() => console.log('Content generated')}
                      />
                    </Suspense>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Main Content Display */}
        {featuresTab === 'core' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {state.activeTab === 'services' && Array.isArray(state.services) && state.services.map((service) => (
              <Card key={service._id || service.id} className="bg-white/10 backdrop-blur-lg border-white/20 transform  transition-transform duration-200">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-white mb-2">{service.title}</h3>
                  <p className="text-white/70 mb-4">{service.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-green-400 font-bold">${service.price}</span>
                    <Link to={`/service/${service._id || service.id}`}>
                      <Button className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}

            {state.activeTab === 'products' && Array.isArray(state.products) && state.products.map((product) => (
              <Card key={product._id || product.id} className="bg-white/10 backdrop-blur-lg border-white/20 transform  transition-transform duration-200">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-white mb-2">{product.title}</h3>
                  <p className="text-white/70 mb-4">{product.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-green-400 font-bold">${product.price}</span>
                    <Link to={`/product/${product._id || product.id}`}>
                      <Button className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}

            {state.activeTab === 'liveBids' && Array.isArray(state.liveBids) && state.liveBids.map((bid) => (
              <Card key={bid._id || bid.id} className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-white mb-2">{bid.jobTitle || 'Job Bid'}</h3>
                  <p className="text-white/70 mb-2">Bid Amount: <span className="text-green-400">${bid.bidAmount}</span></p>
                  <p className="text-white/70 mb-4">Provider: {bid.providerName}</p>
                  <Badge className="bg-yellow-500/20 text-yellow-400">
                    {bid.status || 'pending'}
                  </Badge>
                </CardContent>
              </Card>
            ))}

            {state.activeTab === 'providers' && Array.isArray(state.providers) && state.providers.map((provider) => (
              <Card key={provider._id || provider.id} className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-white mb-2">{provider.name}</h3>
                  <p className="text-white/70 mb-2">Trade: {provider.trade}</p>
                  <p className="text-white/70 mb-4">Rating: ⭐ {provider.rating || 'N/A'}</p>
                  <Link to={`/provider/${provider._id || provider.id}`}>
                    <Button className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm">
                      View Profile
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Loading States */}
        {state.loading[state.activeTab] && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <Card key={i} className="bg-white/10 backdrop-blur-lg border-white/20">
                <CardContent className="p-6">
                  <div className="animate-pulse">
                    <div className="h-4 bg-white/20 rounded mb-2"></div>
                    <div className="h-3 bg-white/20 rounded mb-4"></div>
                    <div className="h-8 bg-white/20 rounded"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Empty States */}
        {!state.loading[state.activeTab] && state[state.activeTab] && Array.isArray(state[state.activeTab]) && state[state.activeTab].length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">
              {state.activeTab === 'services' ? '🔧' :
               state.activeTab === 'products' ? '🛒' :
               state.activeTab === 'liveBids' ? '💼' :
               state.activeTab === 'providers' ? '👥' : '📭'}
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              No {state.activeTab} found
            </h3>
            <p className="text-white/70">
              {state.activeTab === 'services' ? 'Check back later for new services' :
               state.activeTab === 'products' ? 'Check back later for new products' :
               state.activeTab === 'liveBids' ? 'No active bids at the moment' :
               state.activeTab === 'providers' ? 'No providers available right now' : 'Nothing to show'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;